import { useState, useRef, useEffect } from "react";

interface UsageTrendData {
  month: string;
  actual: number;
  predicted: number;
  budget: number;
}

const usageTrendData: UsageTrendData[] = [
  { month: "1月", actual: 25680, predicted: 26000, budget: 28000 },
  { month: "2月", actual: 22560, predicted: 23000, budget: 25000 },
  { month: "3月", actual: 28560, predicted: 27500, budget: 30000 },
  { month: "4月", actual: 25680, predicted: 26800, budget: 28000 },
  { month: "5月", actual: 22800, predicted: 23500, budget: 25000 },
  { month: "6月", actual: 20560, predicted: 21000, budget: 22000 },
  { month: "7月", actual: 21200, predicted: 22000, budget: 23000 },
  { month: "8月", actual: 23400, predicted: 24000, budget: 25000 },
  { month: "9月", actual: 26800, predicted: 27000, budget: 28000 },
  { month: "10月", actual: 28900, predicted: 29000, budget: 30000 },
  { month: "11月", actual: 30200, predicted: 30500, budget: 32000 },
  { month: "12月", actual: 33256, predicted: 33000, budget: 35000 },
];

const peakHours = [
  { hour: "06:00", usage: 12 }, { hour: "07:00", usage: 45 }, { hour: "08:00", usage: 78 },
  { hour: "09:00", usage: 92 }, { hour: "10:00", usage: 88 }, { hour: "11:00", usage: 95 },
  { hour: "12:00", usage: 100 }, { hour: "13:00", usage: 82 }, { hour: "14:00", usage: 75 },
  { hour: "15:00", usage: 70 }, { hour: "16:00", usage: 68 }, { hour: "17:00", usage: 72 },
  { hour: "18:00", usage: 85 }, { hour: "19:00", usage: 60 }, { hour: "20:00", usage: 35 },
  { hour: "21:00", usage: 18 }, { hour: "22:00", usage: 8 }, { hour: "23:00", usage: 5 },
];

const contractHistory = [
  { id: "CON-2020-001", type: "首次签约", startDate: "2020-03-15", endDate: "2022-03-14", amount: "¥1,800,000/年", status: "已到期", note: "初始合同，基础供气协议" },
  { id: "CON-2022-001", type: "续签合同", startDate: "2022-03-15", endDate: "2024-03-14", amount: "¥2,100,000/年", status: "已到期", note: "价格上调16.7%，增加峰谷气价条款" },
  { id: "CON-2024-001", type: "续签合同", startDate: "2024-03-15", endDate: "2026-03-14", amount: "¥2,400,000/年", status: "已到期", note: "增加应急供气保障条款" },
  { id: "CON-2026-001", type: "续签合同（进行中）", startDate: "2026-03-15", endDate: "2028-03-14", amount: "¥2,650,000/年", status: "谈判中", note: "AI预测续签金额区间：¥255-280万/年" },
];

function TrendChart() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const padL = 45, padR = 15, padT = 20, padB = 25;
    const cw = w - padL - padR;
    const ch = h - padT - padB;
    const allVals = [...usageTrendData.map(d => d.actual), ...usageTrendData.map(d => d.predicted), ...usageTrendData.map(d => d.budget)];
    const minV = Math.min(...allVals) - 2000;
    const maxV = Math.max(...allVals) + 2000;
    const range = maxV - minV;

    // Grid
    for (let i = 0; i <= 4; i++) {
      const y = padT + (ch / 4) * i;
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(w - padR, y);
      ctx.strokeStyle = "rgba(0,0,0,0.06)"; ctx.lineWidth = 1; ctx.stroke();
      const val = maxV - (range / 4) * i;
      ctx.fillStyle = "#94a3b8"; ctx.font = "9px sans-serif"; ctx.textAlign = "right";
      ctx.fillText((val / 10000).toFixed(1) + "万", padL - 4, y + 3);
    }

    // Month labels
    usageTrendData.forEach((d, i) => {
      const x = padL + (i / (usageTrendData.length - 1)) * cw;
      ctx.fillStyle = "#94a3b8"; ctx.font = "9px sans-serif"; ctx.textAlign = "center";
      ctx.fillText(d.month, x, h - 6);
    });

    // Budget area
    ctx.beginPath();
    usageTrendData.forEach((d, i) => {
      const x = padL + (i / (usageTrendData.length - 1)) * cw;
      const y = padT + ch - ((d.budget - minV) / range) * ch;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.lineTo(padL + cw, padT + ch); ctx.lineTo(padL, padT + ch); ctx.closePath();
    ctx.fillStyle = "rgba(148,163,184,0.1)"; ctx.fill();

    // Budget line
    ctx.beginPath(); ctx.setLineDash([5, 4]);
    usageTrendData.forEach((d, i) => {
      const x = padL + (i / (usageTrendData.length - 1)) * cw;
      const y = padT + ch - ((d.budget - minV) / range) * ch;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = "#94a3b8"; ctx.lineWidth = 1.5; ctx.stroke(); ctx.setLineDash([]);

    // Predicted line
    ctx.beginPath(); ctx.setLineDash([4, 3]);
    usageTrendData.forEach((d, i) => {
      const x = padL + (i / (usageTrendData.length - 1)) * cw;
      const y = padT + ch - ((d.predicted - minV) / range) * ch;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = "rgba(232,83,26,0.5)"; ctx.lineWidth = 1.5; ctx.stroke(); ctx.setLineDash([]);

    // Actual area
    ctx.beginPath();
    usageTrendData.forEach((d, i) => {
      const x = padL + (i / (usageTrendData.length - 1)) * cw;
      const y = padT + ch - ((d.actual - minV) / range) * ch;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    const lastX = padL + cw;
    const lastY = padT + ch - ((usageTrendData[usageTrendData.length - 1].actual - minV) / range) * ch;
    ctx.lineTo(lastX, padT + ch); ctx.lineTo(padL, padT + ch); ctx.closePath();
    const grad = ctx.createLinearGradient(0, padT, 0, padT + ch);
    grad.addColorStop(0, "rgba(232,83,26,0.2)"); grad.addColorStop(1, "rgba(232,83,26,0)");
    ctx.fillStyle = grad; ctx.fill();

    // Actual line
    ctx.beginPath();
    usageTrendData.forEach((d, i) => {
      const x = padL + (i / (usageTrendData.length - 1)) * cw;
      const y = padT + ch - ((d.actual - minV) / range) * ch;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = "#E8531A"; ctx.lineWidth = 2.5; ctx.lineJoin = "round"; ctx.stroke();

    // Dots
    usageTrendData.forEach((d, i) => {
      const x = padL + (i / (usageTrendData.length - 1)) * cw;
      const y = padT + ch - ((d.actual - minV) / range) * ch;
      ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fillStyle = "#E8531A"; ctx.fill();
    });

    // Legend
    ctx.fillStyle = "#E8531A"; ctx.fillRect(padL, 6, 14, 3);
    ctx.fillStyle = "#64748b"; ctx.font = "9px sans-serif"; ctx.textAlign = "left";
    ctx.fillText("实际用气量", padL + 18, 10);
    ctx.setLineDash([4, 3]); ctx.strokeStyle = "rgba(232,83,26,0.5)"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(padL + 90, 8); ctx.lineTo(padL + 104, 8); ctx.stroke(); ctx.setLineDash([]);
    ctx.fillStyle = "#64748b"; ctx.fillText("AI预测值", padL + 108, 10);
    ctx.setLineDash([5, 4]); ctx.strokeStyle = "#94a3b8"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(padL + 165, 8); ctx.lineTo(padL + 179, 8); ctx.stroke(); ctx.setLineDash([]);
    ctx.fillStyle = "#64748b"; ctx.fillText("预算上限", padL + 183, 10);
  }, []);
  return <canvas ref={canvasRef} width={700} height={200} className="w-full" />;
}

function PeakHourChart() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width; const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    const pad = { l: 8, r: 8, t: 8, b: 20 };
    const cw = w - pad.l - pad.r;
    const ch = h - pad.t - pad.b;
    const barW = cw / peakHours.length;

    peakHours.forEach((d, i) => {
      const x = pad.l + i * barW + barW * 0.1;
      const bw = barW * 0.8;
      const barH = (d.usage / 100) * ch;
      const y = pad.t + ch - barH;
      const isPeak = d.usage >= 80;
      const grad = ctx.createLinearGradient(x, y, x, pad.t + ch);
      grad.addColorStop(0, isPeak ? "#E8531A" : "rgba(232,83,26,0.5)");
      grad.addColorStop(1, isPeak ? "rgba(232,83,26,0.3)" : "rgba(232,83,26,0.1)");
      ctx.fillStyle = grad;
      ctx.beginPath(); ctx.roundRect(x, y, bw, barH, 2); ctx.fill();
      if (i % 3 === 0) {
        ctx.fillStyle = "#94a3b8"; ctx.font = "8px sans-serif"; ctx.textAlign = "center";
        ctx.fillText(d.hour.slice(0, 5), x + bw / 2, h - 4);
      }
    });
  }, []);
  return <canvas ref={canvasRef} width={340} height={100} className="w-full" />;
}

export default function CommercialPortrait() {
  const [activeTab, setActiveTab] = useState<"portrait" | "trend" | "contract">("portrait");
  const [aiReport, setAiReport] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [selectedContract, setSelectedContract] = useState<typeof contractHistory[0] | null>(null);

  const generateAIReport = () => {
    setAiGenerating(true);
    setAiReport("");
    const text = `【AI大用户画像分析报告】
用户：北京燃气热力工程设计院 | 生成时间：2026-04-10

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
一、用户画像综合评估
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• 用户类型：特殊行业大用户（年用气量 >30万m³）
• 用气稳定性：★★★★★（极高，月度波动率 <8%）
• 缴费信用：★★★★★（从未逾期，自动代扣）
• 安全合规：A级（安全评分 95分）
• 综合价值评级：VIP-A+（最高级别）

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
二、用气行为分析
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• 用气高峰：工作日 09:00-12:00（占日用气量42%）
• 季节规律：冬季（11-1月）用气量较夏季高出58%
• 年均用气量：285,680 m³（近3年年均增长率 6.2%）
• 本月用气：25,680 m³，较去年同期 +3.2%
• AI预测下月用气量：26,800 m³（±5%置信区间）

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
三、合同续签AI建议
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• 当前合同状态：谈判中（2026-03-15起）
• AI建议续签金额：¥265-280万/年（较上期+10.4-16.7%）
• 建议合同期限：3年（2026-2029）
• 特殊条款建议：
  1. 增加阶梯气价优惠（年用气量>30万m³享受9折）
  2. 保障供气优先级（应急情况下优先保障）
  3. 增加智能计量升级服务
• 谈判策略：该用户为A+级VIP，建议给予适当价格优惠换取长期合同

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
四、风险评估
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• 欠费风险：极低（历史缴费率100%）
• 安全风险：低（设备状态良好，安检合规）
• 流失风险：低（合作6年，粘性高）
• 用气异常风险：低（用气模式稳定）`;

    let i = 0;
    const interval = setInterval(() => {
      if (i < text.length) { setAiReport(prev => prev + text[i]); i++; }
      else { clearInterval(interval); setAiGenerating(false); }
    }, 15);
  };

  return (
    <div className="space-y-4">
      {/* Tab switcher */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {[
          { key: "portrait", label: "大用户画像", icon: "ri-user-star-line" },
          { key: "trend", label: "用气趋势预测", icon: "ri-line-chart-line" },
          { key: "contract", label: "合同管理", icon: "ri-file-text-line" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key as typeof activeTab)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === t.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
          >
            <i className={t.icon}></i>{t.label}
          </button>
        ))}
      </div>

      {/* Portrait Tab */}
      {activeTab === "portrait" && (
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-8 space-y-4">
            {/* Radar chart simulation */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-user-star-line text-[#E8531A]"></i>
                <h3 className="text-sm font-bold text-gray-800">大用户综合画像</h3>
                <span className="ml-auto text-xs bg-orange-50 text-[#E8531A] px-2 py-0.5 rounded-full font-medium">VIP-A+ 级</span>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                {[
                  { label: "用气稳定性", score: 96, icon: "ri-bar-chart-line", color: "text-green-600", bg: "bg-green-50" },
                  { label: "缴费信用", score: 100, icon: "ri-shield-check-line", color: "text-[#E8531A]", bg: "bg-orange-50" },
                  { label: "安全合规", score: 95, icon: "ri-verified-badge-line", color: "text-teal-600", bg: "bg-teal-50" },
                  { label: "用气规模", score: 88, icon: "ri-fire-line", color: "text-amber-600", bg: "bg-amber-50" },
                  { label: "合作年限", score: 92, icon: "ri-time-line", color: "text-cyan-600", bg: "bg-cyan-50" },
                  { label: "综合价值", score: 94, icon: "ri-star-line", color: "text-purple-600", bg: "bg-purple-50" },
                ].map((item, i) => (
                  <div key={i} className={`${item.bg} rounded-xl p-3`}>
                    <div className="flex items-center gap-2 mb-2">
                      <i className={`${item.icon} ${item.color} text-sm`}></i>
                      <span className="text-xs font-medium text-gray-700">{item.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white/60 rounded-full h-2">
                        <div className="h-2 rounded-full" style={{ width: `${item.score}%`, backgroundColor: item.score >= 90 ? "#22c55e" : "#f59e0b" }} />
                      </div>
                      <span className={`text-sm font-bold ${item.color}`}>{item.score}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Peak hour chart */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-700">用气高峰时段分布</span>
                  <span className="text-xs text-gray-400">工作日平均</span>
                </div>
                <PeakHourChart />
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#E8531A]"></div><span>高峰时段（≥80%）</span></div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#E8531A]/40"></div><span>普通时段</span></div>
                </div>
              </div>
            </div>

            {/* AI Report */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-robot-2-line text-[#E8531A]"></i>
                <h3 className="text-sm font-bold text-gray-800">AI大用户画像分析报告</h3>
              </div>
              {aiReport ? (
                <div className="bg-gray-50 rounded-xl p-4 max-h-64 overflow-y-auto mb-3">
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{aiReport}</pre>
                  {aiGenerating && <span className="inline-block w-1 h-3 bg-[#E8531A] animate-pulse ml-0.5"></span>}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-6 text-center mb-3">
                  <i className="ri-robot-2-line text-gray-300 text-3xl mb-2 block"></i>
                  <p className="text-gray-400 text-sm">点击生成AI画像分析报告</p>
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={generateAIReport} disabled={aiGenerating} className="flex items-center gap-1.5 bg-[#E8531A] text-white px-4 py-2 rounded-lg text-xs font-medium cursor-pointer hover:bg-[#d44a15] whitespace-nowrap disabled:opacity-70">
                  <i className={`${aiGenerating ? "ri-loader-4-line animate-spin" : "ri-robot-2-line"}`}></i>
                  {aiGenerating ? "生成中..." : "生成AI报告"}
                </button>
                {aiReport && !aiGenerating && (
                  <button className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                    <i className="ri-download-line"></i>导出PDF
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="col-span-4 space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">用气行为标签</h4>
              <div className="flex flex-wrap gap-2">
                {["高稳定性", "冬季高峰", "工作日用气", "大用量", "准时缴费", "安全合规", "长期合作", "VIP客户", "特殊行业", "自动代扣"].map((tag, i) => (
                  <span key={i} className={`text-xs px-2.5 py-1 rounded-full font-medium ${i < 4 ? "bg-orange-50 text-[#E8531A]" : "bg-gray-100 text-gray-600"}`}>{tag}</span>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">关键指标</h4>
              <div className="space-y-3">
                {[
                  { label: "合作年限", value: "6年", icon: "ri-time-line", color: "text-cyan-600" },
                  { label: "年均用气量", value: "28.5万m³", icon: "ri-fire-line", color: "text-[#E8531A]" },
                  { label: "年均合同金额", value: "¥240万", icon: "ri-money-cny-circle-line", color: "text-green-600" },
                  { label: "历史缴费率", value: "100%", icon: "ri-shield-check-line", color: "text-green-600" },
                  { label: "安全评分", value: "95分", icon: "ri-verified-badge-line", color: "text-teal-600" },
                  { label: "设备台数", value: "3台", icon: "ri-cpu-line", color: "text-amber-600" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className={`${item.icon} ${item.color} text-sm`}></i>
                      <span className="text-xs text-gray-500">{item.label}</span>
                    </div>
                    <span className={`text-sm font-bold ${item.color}`}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border border-orange-100 p-4">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-robot-2-line text-[#E8531A] text-sm"></i>
                <span className="text-xs font-semibold text-[#E8531A]">AI服务建议</span>
              </div>
              <div className="space-y-2 text-xs text-gray-600">
                <div className="flex items-start gap-1.5">
                  <i className="ri-check-line text-green-500 mt-0.5 flex-shrink-0"></i>
                  <span>提供专属客户经理服务</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <i className="ri-check-line text-green-500 mt-0.5 flex-shrink-0"></i>
                  <span>优先安排安检和维修服务</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <i className="ri-check-line text-green-500 mt-0.5 flex-shrink-0"></i>
                  <span>提供月度用气分析报告</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <i className="ri-check-line text-green-500 mt-0.5 flex-shrink-0"></i>
                  <span>续签合同给予阶梯气价优惠</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trend Tab */}
      {activeTab === "trend" && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <i className="ri-line-chart-line text-[#E8531A]"></i>
                <h3 className="text-sm font-bold text-gray-800">年度用气量趋势与AI预测（m³）</h3>
              </div>
              <div className="flex items-center gap-2 text-xs bg-orange-50 text-[#E8531A] px-3 py-1.5 rounded-full">
                <i className="ri-robot-2-line"></i>
                AI预测误差 &lt;3.5%
              </div>
            </div>
            <TrendChart />
          </div>

          <div className="grid grid-cols-3 gap-4">
            {[
              { title: "下月预测", value: "26,800 m³", change: "+4.4%", desc: "较本月增长，冬季结束后用气量回升", icon: "ri-arrow-up-line", color: "text-green-600", bg: "bg-green-50" },
              { title: "下季度预测", value: "78,500 m³", change: "+2.1%", desc: "Q2用气量预计小幅增长，夏季前保持稳定", icon: "ri-bar-chart-line", color: "text-[#E8531A]", bg: "bg-orange-50" },
              { title: "全年预测", value: "295,000 m³", change: "+3.3%", desc: "全年用气量预计较去年增长3.3%，建议提前锁定气源", icon: "ri-calendar-line", color: "text-amber-600", bg: "bg-amber-50" },
            ].map((item, i) => (
              <div key={i} className={`${item.bg} rounded-2xl p-4`}>
                <div className="flex items-center gap-2 mb-2">
                  <i className={`${item.icon} ${item.color} text-sm`}></i>
                  <span className="text-xs font-semibold text-gray-700">{item.title}</span>
                </div>
                <div className={`text-2xl font-bold ${item.color} mb-1`}>{item.value}</div>
                <div className="text-xs text-gray-500 mb-2">{item.change} vs 去年同期</div>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-robot-2-line text-[#E8531A]"></i>
              <h3 className="text-sm font-bold text-gray-800">AI用气趋势分析</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-600">季节性规律</h4>
                {[
                  { season: "春季（3-5月）", usage: "22,000-26,000 m³/月", trend: "平稳" },
                  { season: "夏季（6-8月）", usage: "20,000-24,000 m³/月", trend: "低谷" },
                  { season: "秋季（9-11月）", usage: "26,000-30,000 m³/月", trend: "上升" },
                  { season: "冬季（12-2月）", usage: "30,000-34,000 m³/月", trend: "高峰" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl">
                    <div>
                      <div className="text-xs font-medium text-gray-700">{item.season}</div>
                      <div className="text-xs text-gray-400">{item.usage}</div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.trend === "高峰" ? "bg-red-50 text-red-600" : item.trend === "低谷" ? "bg-green-50 text-green-600" : item.trend === "上升" ? "bg-amber-50 text-amber-600" : "bg-gray-100 text-gray-500"}`}>{item.trend}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-600">AI预测置信度</h4>
                {[
                  { period: "未来1个月", confidence: 94, accuracy: "±3.5%" },
                  { period: "未来3个月", confidence: 87, accuracy: "±6.2%" },
                  { period: "未来6个月", confidence: 78, accuracy: "±9.8%" },
                  { period: "未来12个月", confidence: 68, accuracy: "±14.5%" },
                ].map((item, i) => (
                  <div key={i} className="p-2.5 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium text-gray-700">{item.period}</span>
                      <span className="text-xs text-gray-400">误差 {item.accuracy}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-[#E8531A]" style={{ width: `${item.confidence}%` }} />
                      </div>
                      <span className="text-xs font-bold text-[#E8531A]">{item.confidence}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contract Tab */}
      {activeTab === "contract" && (
        <div className="space-y-4">
          {/* Contract timeline */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <i className="ri-file-text-line text-[#E8531A]"></i>
                <h3 className="text-sm font-bold text-gray-800">合同历史与续签管理</h3>
              </div>
              <button className="flex items-center gap-1.5 bg-[#E8531A] text-white px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">
                <i className="ri-add-line"></i>新建合同
              </button>
            </div>

            <div className="relative">
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-100"></div>
              {contractHistory.map((contract, i) => (
                <div key={i} className="relative flex gap-4 mb-4 last:mb-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${contract.status === "谈判中" ? "bg-amber-100" : "bg-gray-100"}`}>
                    <i className={`ri-file-text-line text-sm ${contract.status === "谈判中" ? "text-amber-600" : "text-gray-400"}`}></i>
                  </div>
                  <div
                    className={`flex-1 p-4 rounded-xl border cursor-pointer transition-all ${selectedContract?.id === contract.id ? "border-[#E8531A]/30 bg-orange-50/30" : "border-gray-100 bg-gray-50 hover:border-gray-200"}`}
                    onClick={() => setSelectedContract(selectedContract?.id === contract.id ? null : contract)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-800">{contract.type}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${contract.status === "谈判中" ? "bg-amber-50 text-amber-600" : "bg-gray-100 text-gray-500"}`}>{contract.status}</span>
                      </div>
                      <span className="text-sm font-bold text-[#E8531A]">{contract.amount}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span>{contract.startDate} ~ {contract.endDate}</span>
                      <span>·</span>
                      <span>{contract.id}</span>
                    </div>
                    {selectedContract?.id === contract.id && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-600">{contract.note}</p>
                        {contract.status === "谈判中" && (
                          <div className="mt-2 bg-amber-50 rounded-lg p-2.5 flex items-start gap-2">
                            <i className="ri-robot-2-line text-amber-600 text-sm flex-shrink-0"></i>
                            <p className="text-xs text-amber-700">{contract.note}</p>
                          </div>
                        )}
                        <div className="flex gap-2 mt-3">
                          <button className="flex items-center gap-1 text-xs text-[#E8531A] border border-[#E8531A]/30 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-orange-50 whitespace-nowrap">
                            <i className="ri-eye-line"></i>查看合同
                          </button>
                          {contract.status === "谈判中" && (
                            <button className="flex items-center gap-1 text-xs bg-[#E8531A] text-white px-3 py-1.5 rounded-lg cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">
                              <i className="ri-edit-line"></i>编辑条款
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI contract analysis */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl border border-orange-100 p-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-robot-2-line text-[#E8531A] text-lg"></i>
              <h3 className="text-sm font-bold text-gray-800">AI合同续签分析</h3>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-3">
                <div className="text-xs text-gray-400 mb-1">AI建议续签金额</div>
                <div className="text-lg font-bold text-[#E8531A]">¥265-280万/年</div>
                <div className="text-xs text-gray-400 mt-0.5">较上期 +10.4-16.7%</div>
              </div>
              <div className="bg-white rounded-xl p-3">
                <div className="text-xs text-gray-400 mb-1">建议合同期限</div>
                <div className="text-lg font-bold text-gray-800">3年</div>
                <div className="text-xs text-gray-400 mt-0.5">2026-03-15 ~ 2029-03-14</div>
              </div>
              <div className="bg-white rounded-xl p-3">
                <div className="text-xs text-gray-400 mb-1">续签成功概率</div>
                <div className="text-lg font-bold text-green-600">96%</div>
                <div className="text-xs text-gray-400 mt-0.5">基于历史合作记录</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
